import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { OccurrenceRow } from '@/components/teacher/schedule/calendar/OccurrenceRow';
import { supabase } from '@/integrations/supabase/client';
import type { WeeklyItem, ClassTemplateEvent } from '@/types/schedule';
import { useTeacherProjectedSchedule } from '@/hooks/useTeacherProjectedSchedule';

interface Props {
  teacherCode: string;
  weekStartISO: string;
}

export const WeeklySchedule: React.FC<Props> = ({ teacherCode, weekStartISO }) => {
  const weekEndISO = useMemo(() => {
    const end = new Date(weekStartISO);
    end.setDate(end.getDate() + 6);
    return end.toISOString().slice(0, 10);
  }, [weekStartISO]);
  const projectedQuery = useTeacherProjectedSchedule(teacherCode, weekStartISO, weekEndISO);
  const [events, setEvents] = useState<ClassTemplateEvent[]>([]);

  const fetchEvents = useCallback(async () => {
    if (!teacherCode || !weekStartISO) return;
    const endDate = new Date(weekStartISO);
    endDate.setDate(endDate.getDate() + 6);
    const { data } = await supabase
      .from('v_class_template_events')
      .select('*')
      .eq('requested_by', teacherCode)
      .gte('event_date', weekStartISO)
      .lte('event_date', endDate.toISOString().slice(0, 10));
    setEvents((data ?? []) as ClassTemplateEvent[]);
  }, [teacherCode, weekStartISO]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const combined = useMemo(() => (projectedQuery.data?.items ?? []) as WeeklyItem[], [projectedQuery.data]);

  const grouped = useMemo(() => {
    const map: Record<string, WeeklyItem[]> = {};
    for (const item of combined) {
      const d = item.date;
      map[d] = map[d] || [];
      map[d].push(item);
    }
    return map;
  }, [combined]);

  return (
    <div className="space-y-4">
      {projectedQuery.isError && (
        <div className="text-sm text-red-600">โหลดข้อมูลตารางสอนไม่สำเร็จ</div>
      )}
      {Object.entries(grouped).map(([date, items]) => (
        <Card key={date}>
          <CardHeader>
            <CardTitle className="text-base">{new Date(date).toLocaleDateString('th-TH')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {items.sort((a,b)=>a.start_time.localeCompare(b.start_time)).map((item) => (
              <OccurrenceRow
                key={`${item.fixed_job_id}-${item.date}-${item.occurrence_id || 'proj'}`}
                item={item}
                teacherCode={teacherCode}
                events={events}
                onRefresh={fetchEvents}
              />
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
